import { Component, ComponentRef, computed, effect, inject, input, signal, untracked, ViewChild } from '@angular/core';
import { MainImageComponent } from '../main-image/main-image.component';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { ConfiguredItem } from 'src/app/interfaces/configured-item';
import { MatDialog } from '@angular/material/dialog';
import { SkuDialogComponent } from '../sku.dialog/sku.dialog.component';
import { InvalidSkuDialogComponent } from '../invalid-sku.dialog/invalid-sku.dialog.component';
import { formatFingerSizeForB2BSKU } from 'src/app/utils/configured-item.util';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { PriceProviderService } from 'src/app/providers/price/price.provider.service';
import { assert } from 'src/app/utils/assert.util';
import { CheckoutService } from '../../services/checkout.service';
import { SkuMapService } from 'src/app/services/sku-map/sku-map.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { ResetDialogComponent } from '../reset.dialog/reset.dialog.component';
import { Navigation2Service } from '../../services/navigation2.service';
import { Subscription } from 'rxjs';
import { PriceService } from 'src/app/services/price/price.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { MainTabItem } from 'src/app/interfaces/main-tab-item';
import { BookmarksComponent } from '../bookmarks/bookmarks.component';
import { BookmarksService } from '../../services/bookmarks.service';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { TopBarService } from 'src/app/services/top-bar-service/top-bar-service.service';
import { StyleImagesProviderService } from 'src/app/providers/style-images/style-images.provider.service';
import { StyleImage } from 'src/app/interfaces/style-image';
import { getAWSAssetURL } from 'src/app/constants/assets';
import { StyleImageGalleryComponent } from '../style-image-gallery/style-image-gallery.component';
import { TopBarItem } from 'src/app/interfaces/top-bar-item';
import { ShareSectionComponent } from '../share-section/share-section/share-section.component';
import { ShareService } from 'src/app/services/share/share.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-previewer',
  templateUrl: './previewer.component.html',
  styleUrls: ['./previewer.component.scss']
})
export class PreviewerComponent {
  private clipBoard = inject(Clipboard);
  private shareService = inject(ShareService);
  private styleImagesProvider = inject(StyleImagesProviderService);
  private topBarService = inject(TopBarService);
  private bookmarksService = inject(BookmarksService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private loadingService = inject(LoadingService);
  private skuMapService = inject(SkuMapService);
  private configuredItemService = inject(ConfiguredItemService);
  private checkoutService = inject(CheckoutService);
  private navigationService = inject(Navigation2Service);
  private priceService = inject(PriceService);
  private sizesService = inject(SizesService);
  private snackBar = inject(MatSnackBar);
  dialog = inject(MatDialog);
  priceProvider = inject(PriceProviderService);
  selectedAttributeService = inject(SelectedAttributeService);
  @ViewChild(MainImageComponent) mainImage?: MainImageComponent;
  styleImages = signal<StyleImage[]>([]);
  tinyThumbnailURLs = computed(() => this.styleImages().slice(0,3).map(image => getAWSAssetURL(image.fileName.replace('{{size}}', 'tiny'))));
  wasModelSuccess = signal(true);
  skuToShow = signal("");
  isSharing = input(false);
  wholesalePriceToShow = computed(() => this.priceService.wholesalePriceToShow());
  retailPrice = computed(() =>  this.priceService.retailPrice());
  shouldShowWholesalePrice = signal<boolean>(false);
  canUndo = computed(() => this.selectedAttributeService.undoList().length > 0);
  canRedo = computed(() => this.selectedAttributeService.redoList().length > 0);
  isRetailerMode = computed(() => this.checkoutService.isRetailerOnly());
  b2bDataSubscription: Subscription | null = null;
  shouldShowPrice = computed(() => !this.isSharing() && this.navigationService.activeView().title !== 'Retailer Summary');
  // tabItems = signal<MainTabItem[]>([
  //   {
  //     icon: 'home',
  //     hover: 'Build',
  //     view: null
  //   },
  //   {
  //     icon: 'favorite',
  //     hover: 'Favorites',
  //     view: { title: 'Favorites', type: BookmarksComponent, doesAppearInProgressBar: false }
  //   }
  // ]);
  // activeTab = signal(this.tabItems()[0]);
  activeBarItem = computed(() => this.topBarService.activeItem());
  tabItems = computed(() => this.topBarService.items().filter((tabItem) => tabItem.data?.shouldShowInMobile));
  customSKU = computed(() => this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
  bookmarkKey = computed(() => {
    const sku = this.customSKU();
    const haloSKU = !this.configuredItemService.isCustomSKU(this.skuToShow()) && !this.configuredItemService.isCustomStyle(this.skuToShow()) ? this.skuToShow() : '';
    return `${sku}:${haloSKU}`;
  });
  bookmarks = computed(() => this.bookmarksService.bookmarks());
    prevItem = signal<ConfiguredItem>({
      design: null,
      form: null,
      primaryMaterial: null,
      secondaryMaterial: null,
      width: null,
      engravingType: null,
      engravingText: null,
      size: null,
      gemstoneType: null,
      gemstoneArrangement: null,
      gemstoneSize: null,
      gemstoneSetting: null,
      gemstoneShape: null
    });

  constructor() {
    effect(() => {
      this.selectedAttributeService.configuredItem();
      this.wasModelSuccess.set(true);
    }, { allowSignalWrites: true });

    effect(async () => {
      if (!this.isSharing()) {
        this.loadingService.load('previewer::price');
        const pricingObject = this.selectedAttributeService.pricingObject();
        if (pricingObject) {
          await this.priceService.update(pricingObject, untracked(() => this.selectedAttributeService.configuredItem()));
        }
        this.loadingService.done('previewer::price');
      }
    }, { allowSignalWrites: true });

    effect(async () => {
      const prevItem = untracked(() => this.prevItem());
      const configuredItem = this.selectedAttributeService.configuredItem();
      const changes = (Object.keys(configuredItem) as (keyof ConfiguredItem)[]).reduce((changes, key) => {
        if (configuredItem[key] !== prevItem[key]) {
          changes.push(key);
        }
        return changes;
      }, [] as string[]);

      if (!changes.every(k => k.includes('engraving') || k.includes('size'))) {
        this.loadingService.load('StyleImageThumbnails::load');
        const images = await this.styleImagesProvider.getP4s(configuredItem);
        this.styleImages.set(images);
        this.loadingService.done('StyleImageThumbnails::load');
      }
      this.prevItem.set(configuredItem);
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    const size = this.selectedAttributeService.getValue().size;
    assert(size, 'No size selected');
    this.shouldShowWholesalePrice.set(!this.checkoutService.b2bData.retailPricingOnly);
    this.skuToShow.set((!this.checkoutService.b2bData.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(this.checkoutService.b2bData.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
    this.b2bDataSubscription = this.checkoutService.subscribeToB2BDataChanges(async value => {
      const size = this.selectedAttributeService.getValue().size;
      assert(size, 'No size selected');
      this.loadingService.load('SKUToShow');
      this.skuToShow.set((!value.haloStyle ? undefined : this.configuredItemService.buildHaloSKU(value.haloStyle, size.value, false)) ?? this.configuredItemService.getNewSKUFromSelectedAttributes(this.selectedAttributeService.configuredItem()));
      this.loadingService.done('SKUToShow');
    });
  }

  ngOnDestroy() {
    this.b2bDataSubscription?.unsubscribe();
  }

  skuClicked() {
    this.dialog.open(SkuDialogComponent, { width: '500px' }).afterClosed().subscribe((v) => {
      if (v) {
        try {
          const newAttributes = this.configuredItemService.getAttributesFromNewSKU(v);
          (Object.keys(newAttributes) as (keyof ConfiguredItem)[]).forEach((k) => {
            (this.selectedAttributeService as any)[`mutate${k.charAt(0).toUpperCase()}${k.slice(1)}`](newAttributes[k]);
          });
        } catch(e) {
          console.error(e);
          this.dialog.open(InvalidSkuDialogComponent, { width: '400px' });
        }
      }
    })
  }

  modelCreated(wasSuccessfull: boolean) {
    this.wasModelSuccess.set(wasSuccessfull)
  }

  async searchSKU(skuOrStyleNumber: string) {
    this.triggerGAEvent('search', 'item_number_search', skuOrStyleNumber);
    this.loadingService.load('previewer:searchSKU');
    const formattedSKUOrStyleNumber = skuOrStyleNumber.toUpperCase();
    try {
      if(!formattedSKUOrStyleNumber.match(/\D/)) {
         this.selectedAttributeService.addConfiguredItemToUndoList(null);
        const mapEntry = await this.skuMapService.getWithShortCode(+formattedSKUOrStyleNumber);
        assert(mapEntry, 'Could not find item with short code');
        await this.selectedAttributeService.setConfiguredItemFromStyle(mapEntry.ringBuilderSKU, mapEntry.fingerSize, undefined, (b2bData) => {
          b2bData.selectedHaloStyleNumber = undefined;
          b2bData.haloStyle = mapEntry.halosku ?? undefined;
          const size = this.sizesService.values().find(s => s.value === mapEntry.fingerSize);
          assert(size, 'Size was null');
          this.selectedAttributeService.pricingObject.set({ style: mapEntry.halosku ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem()), size });
        });
      } else if (this.configuredItemService.isCustomStyle(formattedSKUOrStyleNumber)) {
        this.selectedAttributeService.addConfiguredItemToUndoList(null);
        const mapEntry = await this.skuMapService.getReverse(formattedSKUOrStyleNumber);
        await this.selectedAttributeService.setConfiguredItemFromStyle(formattedSKUOrStyleNumber, undefined, undefined, (b2bData) => {
          b2bData.selectedHaloStyleNumber = undefined;
          b2bData.haloStyle = mapEntry[0]?.halosku;
          const size = this.selectedAttributeService.configuredItem().size;
          assert(size, 'Size was null');
          this.selectedAttributeService.pricingObject.set({ style: mapEntry[0]?.halosku ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem()), size });
        });
      } else if (this.configuredItemService.isCustomSKU(formattedSKUOrStyleNumber)) {
        const size = formattedSKUOrStyleNumber.split('-')?.slice(-1)[0];
        const sizeObject = this.sizesService.values().find(s => formatFingerSizeForB2BSKU(s.value) === size);
        assert(sizeObject, 'Could not match size from sku');
        const mapEntry = await this.skuMapService.getReverse(formattedSKUOrStyleNumber.split('-')?.slice(0, -1)?.join('-'), sizeObject.value);
        this.selectedAttributeService.addConfiguredItemToUndoList(null);
        await this.selectedAttributeService.setConfiguredItemFromSKU(formattedSKUOrStyleNumber, (b2bData) => {
          b2bData.selectedHaloStyleNumber = undefined;
          b2bData.haloStyle = mapEntry[0]?.halosku;
          const size = this.selectedAttributeService.configuredItem().size;
          assert(size, 'Size was null');
          this.selectedAttributeService.pricingObject.set({ style: mapEntry[0]?.halosku ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem()), size });
        });
      } else {
        const fingerSize = formattedSKUOrStyleNumber.match(/(?<size>\d{2}.?\d{0,2}$)/)?.groups?.['size'];
        const replacerRegex = new RegExp(`${fingerSize ?? null}$`);

        const styleNumber = formattedSKUOrStyleNumber.replace(replacerRegex, '');
        const mapEntry = await this.skuMapService.get(styleNumber);
        if (mapEntry) {
          const fingerSizeNumber = +(fingerSize ?? NaN);
          this.selectedAttributeService.addConfiguredItemToUndoList(null);
          await this.selectedAttributeService.setConfiguredItemFromStyle(mapEntry?.ringBuilderSKU, !isNaN(fingerSizeNumber) ? fingerSizeNumber : undefined, undefined, (b2bData) => {
            b2bData.selectedHaloStyleNumber = styleNumber;
            b2bData.haloStyle = styleNumber;
            const size = this.selectedAttributeService.configuredItem().size;
            assert(size, 'Size was null');
            this.selectedAttributeService.pricingObject.set({ style: styleNumber, size });
          });
        } else {
          throw 'Invalid Size'
        }
      }
      this.navigationService.navigateTo(0);
    } catch(e) {
      this.dialog.open(InvalidSkuDialogComponent);
    } finally {
      this.loadingService.done('previewer:searchSKU');
      this.loadingService.done('selected-attribute:set-item-from-style');
    }
  }

  openChat() {
    this.triggerGAEvent('click', 'open_chat', '1');
    (window as any).purechatApi.set('chatbox.expanded', true);
  }

  triggerGAEvent(action: string, title: string, value: string) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent(action, 'previewer', title, value);
    }
  }

  tabItemClicked(tabItem: TopBarItem) {
    if (this.activeBarItem()?.key !== tabItem.key) {
      tabItem.action(tabItem);
    }
  }

  async toggleBookmark() {
    this.loadingService.load('bookmarks::toggle');
    if (this.bookmarks().some(b => b.sku === this.bookmarkKey())) {
      await this.bookmarksService.remove(this.bookmarkKey());
    } else {
      const snapshot = await this.mainImage?.getCurrentRingSnapshot('image/jpeg');
      assert(snapshot, 'Could not get bookmark thumbnail');
      await this.bookmarksService.add(this.bookmarkKey(), snapshot);
    }
    this.loadingService.done('bookmarks::toggle');
  }

  isBookmarkKeyInBookmarks(bookmarkKey: string) {
    // console.log(this.bookmarks(), bookmarkKey)
    return this.bookmarks().some(b => b.sku === bookmarkKey);
  }

  openStyleImageGallery(image: StyleImage) {
    this.dialog.open(StyleImageGalleryComponent, { data: { title: this.selectedAttributeService.configuredItem().design?.displayName, selectedImage: image, images: this.styleImages() }, panelClass: 'GalleryGlobal', enterAnimationDuration: '0ms', exitAnimationDuration: '0ms', autoFocus: false });
  }

  openShareSection() {
    const shareURL = this.shareService.shareLink();
    const shareData = { url: shareURL };
    if ('canShare' in navigator && 'share' in navigator && navigator.canShare(shareData)) {
      navigator.share(shareData);
    } else {
      this.clipBoard.copy(shareURL);
      this.snackBar.open('Share link copied to clipboard', 'Close', { duration: 3000 });
    }
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'copy_share', 'share_link_copy', shareURL);
    }
  }
}
